import { z } from 'zod';
import { PROMPT_MODE, PROMPT_PRESET_KEY } from '../types/prompt.js';

/**
 * "Payload kreatif" yang dihasilkan LLM di setiap mode — PRD §11 (PromptPackage
 * fields), minus scenePrompts (baru diisi Fase 4 saat storyboard compile,
 * lihat PRD §10.1: "Scene-specific Compilation" adalah tahap terpisah setelah
 * storyboard ada — prompt package di Fase 3 baru sampai master-level).
 *
 * INI JUGA JADI `responseSchema` yang dikirim ke Gemini (via zod-to-json-schema)
 * supaya output LLM dipaksa terstruktur — PRD §10.2: "memvalidasi output dengan
 * Zod schema per-mode".
 */
export const promptVersionPayloadSchema = z.object({
  normalizedInput: z
    .string()
    .min(1)
    .describe('Restatement terstruktur dari product facts + brief user, netral tanpa gaya kreatif'),
  masterPrompt: z
    .string()
    .min(1)
    .describe('Prompt kreatif utama, siap dipakai sebagai basis compile scene di Fase 4'),
  negativeConstraints: z
    .array(z.string().min(1))
    .describe('Hal yang harus DIHINDARI (klaim terlarang, visual yang salah, dsb)'),
  voicePrompt: z.string().min(1).describe('Arahan gaya voiceover: nada, tempo, karakter suara'),
  captionInstructions: z.string().min(1).describe('Arahan gaya on-screen caption/subtitle'),
});
export type PromptVersionPayload = z.infer<typeof promptVersionPayloadSchema>;

export const promptVersionSchema = promptVersionPayloadSchema.extend({
  id: z.string().min(1),
  promptPackageId: z.string().min(1),
  version: z.number().int().positive(),
  mode: z.enum(PROMPT_MODE),
  sourceInput: z.string().nullable().optional(),
  provider: z.string().min(1),
  model: z.string().min(1),
  parameters: z.record(z.unknown()).nullable().optional(),
  templateVersion: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type PromptVersion = z.infer<typeof promptVersionSchema>;

export const promptPackageSchema = z.object({
  id: z.string().min(1),
  productId: z.string().min(1),
  presetKey: z.enum(PROMPT_PRESET_KEY).nullable().optional(),
  currentVersion: z.number().int().positive(),
  createdAt: z.string(),
});
export type PromptPackage = z.infer<typeof promptPackageSchema>;

/**
 * Input POST /api/bff/prompt/:mode.
 * - `promptPackageId` ADA → transform versi terbaru package itu (base diambil dari repo, bukan dari client).
 * - `promptPackageId` KOSONG → bootstrap package baru, `productId` wajib.
 * `instruction` = arahan tambahan spesifik untuk panggilan transform ini (opsional, semua mode).
 */
export const promptTransformInputSchema = z
  .object({
    promptPackageId: z.string().min(1).optional(),
    productId: z.string().min(1).optional(),
    rawInput: z.string().max(4000).optional(),
    presetKey: z.enum(PROMPT_PRESET_KEY).optional(),
    instruction: z.string().max(1000).optional(),
  })
  .refine((val) => val.promptPackageId || val.productId, {
    message: 'productId wajib diisi kalau promptPackageId belum ada (bootstrap package baru)',
    path: ['productId'],
  });
export type PromptTransformInput = z.infer<typeof promptTransformInputSchema>;

export const promptPresetSchema = z.object({
  id: z.string().min(1),
  key: z.enum(PROMPT_PRESET_KEY),
  name: z.string().min(1),
  description: z.string().min(1),
  strategyInstruction: z.string().min(1),
  createdAt: z.string(),
});
export type PromptPreset = z.infer<typeof promptPresetSchema>;
